import { Grid } from '@material-ui/core';
import { Body, Icon, Tooltip } from 'copilot-design-system';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { v4 } from 'uuid';

import { NoCompanyIcon, SignatureDateIcon } from 'src/components/icons';
import {
  CONTRACT_COMPONENT_DEFAULT_HEIGHT,
  CONTRACT_COMPONENT_DEFAULT_WIDTH,
  SignatureComponentType,
} from 'src/constants';
import {
  AutoFillFieldKey,
  PredefinedAutofillKeys,
} from 'src/entities/Contract';
import { MapFieldTypeToDefaultProps } from 'src/legacy/components/CustomFieldsMenu';
import { SignatureSidebarButton } from 'src/legacy/components/Signature/SignatureComponents';
import MemoBaseTypography from 'src/legacy/components/Text/BaseTypography';
import { InputType, useGetPortalConfigQuery } from 'src/services/api';
import { useCustomFields } from 'src/services/api/customFieldsApi';
import { CustomFieldEntityType } from 'src/store/clients/types';
import {
  AddComponentAction,
  SetSelectedComponent,
} from 'src/store/signaturePage/actions';
import { ComponentCoordinatesType } from 'src/store/signaturePage/types';
import { getFieldTypeUsingComponentType } from 'src/utils/ContractUtils';
import { toCamelCase } from 'src/utils/StringUtils';
import { isUUID, UUID } from 'src/utils/UUIDUtils';

const SHARE_DATE_AUTOFILL_INPUT_KEY = `{{contract.${PredefinedAutofillKeys.ShareDate}}}`;
const AUTOFILL_INPUT_TOOLTIP_TEXT = `The values will automatically be set based on the client you share a contract with.`;

/**
 * This component renders the contract sidebar auto-fill inputs.
 */
export const AutofillInputs = () => {
  const dispatch = useDispatch();
  const { data: portalConfigEntity } = useGetPortalConfigQuery();

  const customFields = useCustomFields(CustomFieldEntityType.CLIENT);
  const customFieldsMap = customFields;

  /**
   * Handles the component drop action.
   * @param {ComponentCoordinatesType} coords - The coordinates where the component is dropped.
   * @param {SignatureComponentType} componentType - The type of the dropped component.
   * @param {string} autoFillFieldKey - The auto-fill field key.
   */
  const handleComponentDropped = (
    coords: ComponentCoordinatesType,
    componentType: SignatureComponentType,
    autoFillFieldKey: string,
    fieldId: AutoFillFieldKey,
  ) => {
    const key = v4();
    const stagedComponent = {
      componentType,
      inputType: InputType.AutoFill,
      label: autoFillFieldKey,
      name: autoFillFieldKey,
      fieldType: getFieldTypeUsingComponentType(componentType),
      key,
      fields: { key, value: '' },
      ...coords,
      height: CONTRACT_COMPONENT_DEFAULT_HEIGHT,
      width: CONTRACT_COMPONENT_DEFAULT_WIDTH,
      autoFillField: fieldId,
    };

    dispatch(AddComponentAction(stagedComponent));
    dispatch(SetSelectedComponent(stagedComponent.fields.key));
  };

  // List of autofillable contract signature inputs
  const autofillFieldsInfo = useMemo(() => {
    const defaultFields = [
      {
        label: `{{client.${PredefinedAutofillKeys.FullName}}}`,
        type: 'text' as const,
        id: PredefinedAutofillKeys.FullName,
      },
      {
        label: `{{client.${PredefinedAutofillKeys.FirstName}}}`,
        type: 'text' as const,
        id: PredefinedAutofillKeys.FirstName,
      },
      {
        label: `{{client.${PredefinedAutofillKeys.LastName}}}`,
        type: 'text' as const,
        id: PredefinedAutofillKeys.LastName,
      },
      {
        label: `{{client.${PredefinedAutofillKeys.Email}}}`,
        type: 'email' as const,
        id: PredefinedAutofillKeys.Email,
      },
      ...(!portalConfigEntity?.structFields?.disableCompanies
        ? [
            {
              label: `{{client.${PredefinedAutofillKeys.Company}}}`,
              type: 'text' as const,
              id: PredefinedAutofillKeys.Company,
            },
          ]
        : []),
    ];

    // Using client's custom properties list to list autofill inputs
    const portalCustomFields = Object.values(customFieldsMap).map((f) => {
      if (!isUUID(f.id)) {
        console.error(`Invalid UUID for custom field: ${f.id}`);
      }
      return {
        label: `{{client.${toCamelCase(f.name)}}}`,
        type: f.type,
        id: f.id as UUID, // casting since we're doing the check above
      };
    });

    return [...defaultFields, ...portalCustomFields];
  }, [customFieldsMap, portalConfigEntity]);

  return (
    <>
      <div className="flex items-center gap-2 mb-3">
        <MemoBaseTypography fontType="12Medium">
          Autofill fields
        </MemoBaseTypography>
        <Tooltip
          position="bottom"
          triggerClassname="inline-block"
          content={
            <Body size="sm" className="max-w-[288px]">
              {AUTOFILL_INPUT_TOOLTIP_TEXT}
            </Body>
          }
        >
          <Icon icon="Info" className="h-3 w-3 text-gray-small" />
        </Tooltip>
      </div>
      <Grid container spacing={2}>
        {autofillFieldsInfo.map(({ label, type, id }) => (
          <Grid item xs={12} key={label}>
            <SignatureSidebarButton
              className="[&_svg]:h-4"
              label={label}
              onDrop={(coordinates) =>
                handleComponentDropped(
                  coordinates,
                  SignatureComponentType.REQUEST_TEXT,
                  label,
                  id,
                )
              }
              buttonIcon={
                id === PredefinedAutofillKeys.Company
                  ? NoCompanyIcon
                  : MapFieldTypeToDefaultProps[type].icon
              }
              isClientSignatureButton
            />
          </Grid>
        ))}
        <Grid item xs={12}>
          <SignatureSidebarButton
            label={SHARE_DATE_AUTOFILL_INPUT_KEY}
            onDrop={(coordinates) =>
              handleComponentDropped(
                coordinates,
                SignatureComponentType.REQUEST_DATE,
                SHARE_DATE_AUTOFILL_INPUT_KEY,
                PredefinedAutofillKeys.ShareDate,
              )
            }
            buttonIcon={SignatureDateIcon}
            isClientSignatureButton
          />
        </Grid>
      </Grid>
    </>
  );
};
